import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Store, Globe2, Check, ChevronDown, Layers } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { useCanal, type CanalView } from '@/lib/canal'
import { canaisPermitidos } from '@/lib/permissoesCanal'

// Seletor de CANAL de PDV na barra superior (feature 09/07 — isolamento por canal).
// Espelha FilialSelector, mas sem fetch: os canais permitidos vêm das permissões do
// usuário (regra idêntica ao backend em permissoesCanal.ts). O backend IMPÕE o
// canal permitido, então este seletor é conveniência de visão:
//   - permissão dos DOIS canais → toggle (Ambos / PDV-Loja / PDV-Online)
//   - só ONLINE → badge estático "Online" (informa o escopo; força a visão online)
//   - só BALCÃO (maioria legada) → não renderiza nada (evita poluir a barra; o
//     backend já tranca em balcão de qualquer forma)
const OPCOES: { v: CanalView; label: string; icon: typeof Store; cor: string }[] = [
  { v: 'ambos',  label: 'Ambos os canais', icon: Layers,  cor: 'text-ink-500' },
  { v: 'balcao', label: 'PDV-Loja',        icon: Store,   cor: 'text-amber-600' },
  { v: 'online', label: 'PDV-Online',      icon: Globe2,  cor: 'text-violet-600' },
]

export default function CanalSelector() {
  const { user } = useAuth()
  const { canalView, setCanalView } = useCanal()
  const { podeBalcao, podeOnline } = canaisPermitidos(user)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const qc = useQueryClient()
  const temAmbos = podeBalcao && podeOnline

  // Usuário trancado num canal só: força a visão pra aquele canal (mantém header,
  // badge e lógica de tags coerentes). Defesa em profundidade com a imposição no
  // backend. Só age quando a visão diverge do canal permitido.
  useEffect(() => {
    if (temAmbos) return
    const unico: CanalView | null = podeOnline && !podeBalcao ? 'online'
      : podeBalcao && !podeOnline ? 'balcao' : null
    if (unico && canalView !== unico) setCanalView(unico)
  }, [temAmbos, podeBalcao, podeOnline, canalView, setCanalView])

  // Click fora fecha
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  // Só balcão (ou nenhum) → nada na barra.
  if (!temAmbos) {
    if (podeOnline && !podeBalcao) {
      return (
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs border border-violet-200 bg-violet-50 text-violet-700"
             title="Você opera apenas o PDV-Online">
          <Globe2 size={13} /> <span className="font-medium">Online</span>
        </div>
      )
    }
    return null
  }

  const atual = OPCOES.find(o => o.v === canalView) || OPCOES[0]
  const AtualIcon = atual.icon

  const escolher = (v: CanalView) => {
    setCanalView(v)
    setOpen(false)
    // Refetcha tudo com o novo X-Canal (igual FilialSelector ao trocar de filial).
    qc.invalidateQueries()
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs border transition-colors ${
          canalView === 'ambos'
            ? 'hover:bg-ink-100 text-ink-700 border-ink-200'
            : 'bg-ink-50 border-ink-300 text-ink-800 hover:bg-ink-100'
        }`}
        title="Canal de venda exibido (PDV-Loja × PDV-Online)"
      >
        <AtualIcon size={13} className={atual.cor} />
        <span className="font-medium">{canalView === 'ambos' ? 'Ambos' : atual.label}</span>
        <ChevronDown size={12} className={`text-ink-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-ink-200 rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-ink-100 text-xs font-medium text-ink-500 uppercase tracking-wide">
            Canal exibido
          </div>
          {OPCOES.map(o => {
            const OIcon = o.icon
            return (
              <button
                key={o.v}
                onClick={() => escolher(o.v)}
                className={`w-full text-left px-3 py-2.5 hover:bg-ink-50 flex items-center gap-2 border-b border-ink-100 last:border-0 transition-colors ${canalView === o.v ? 'bg-ink-50' : ''}`}
              >
                <OIcon size={14} className={canalView === o.v ? o.cor : 'text-ink-400'} />
                <div className="flex-1">
                  <div className="text-sm font-medium text-ink-900">{o.label}</div>
                  {o.v === 'ambos' && <div className="text-[11px] text-ink-500">Mostra Loja e Online juntos, com tag por venda</div>}
                </div>
                {canalView === o.v && <Check size={14} className="text-ink-600 shrink-0" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
